import React, {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import {Contact, PartialClient, Address, Client} from "../types";
import * as Yup from 'yup'
import {getAddresses, getContacts, postClient, updateClient} from "../api/dataService";
import {handlePostError, isLoadingSpinner} from "../util/Helpers";
import Container from "react-bootstrap/Container";
import {Alert, Modal} from "react-bootstrap";
import {ErrorMessage, Field, Formik} from "formik";
import {Navigate} from "react-router-dom";
import Select from "react-select";
import CreateAddress from "./CreateAddress";
import CreateContact from "./CreateContact";
interface CreateClientProps {
    clientToUpdate? : PartialClient
    closeModal?: ()=>void
    onCreate?: (createdObject: PartialClient) => void
    onUpdate?: (updatedObject: Client) => void
}

const CreateClient: React.FC<CreateClientProps> = (props) => {
    const isUpdating = !!props.clientToUpdate
    const [isLoading, setIsLoading] = useState(true)
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [showSuccess, setShowSuccess] = useState(false)
    const [contactList, setContactList] = useState<Contact[]> ([])
    const [addressList, setAddressList] = useState<Address[]> ([])
    const [showCreateContact, setShowCreateContact] = useState(false)
    const [showCreateAddress, setShowCreateAddress] = useState(false)
    const formikRef = useRef<any>(null)
    const contactSelectRef = useRef<any>(null)
    const addressSelectRef = useRef<any>(null)
    const validationSchema = Yup.object().shape({
        client_name: Yup.string().required('Client Name is required'),
        contact: Yup.mixed().test(
            'is-valid-contact',
            'Client Contact is required',
            value => {
                if (!value) return false;
                if (typeof value === 'string') return value.trim() !== '';
                if (typeof value === 'object' && value !== null) return !!(value as {id: string | number}).id;
                return false;
            }
        ),
        address: Yup.mixed().test(
            'is-valid-address',
            'Address is required',
            value => {
                if (!value) return false;
                if (typeof value === 'string') return value.trim() !== '';
                if (typeof value === 'object' && value !== null) return !!(value as {id: string | number}).id;
                return false;
            }
        ),
    })
    // Create initialValues, ensuring contact and address are properly handled
    const initialValues = props.clientToUpdate ? {
        ...props.clientToUpdate,
        // Keep objects as is - our new validation schema will handle them
        contact: props.clientToUpdate.contact,
        address: props.clientToUpdate.address,
    } : {
        client_name: '',
        contact: undefined,
        address: undefined,
    }

    useEffect(() => {
        Promise.all([getContacts(), getAddresses()])
            .then(([contactsResponse, addressResponse]) => {
                if ('redirectTo' in contactsResponse) return(<Navigate to={contactsResponse.redirectTo}/>)
                else (setContactList(Array.isArray(contactsResponse)? contactsResponse: []))
                if ('redirectTo' in addressResponse) return(<Navigate to={addressResponse.redirectTo}/>)
                else (setAddressList(Array.isArray(addressResponse)? addressResponse: []))

                if(contactList && addressList){
                    setIsLoading( false)
                }
            })
            .catch(error => {
                console.error("Error Fetching Data: ", error)
            })
    },[])

    // This useEffect is no longer needed as we're keeping the original objects in initialValues
    // and our validation schema now accepts both strings and objects

    const handleCloseCreateContact = () => {
        setShowCreateContact(false)
    }

    const handleShowCreateAddress = () => {
        setShowCreateContact(false)
        setShowCreateAddress(true)
    }

    const handleShowCreateContact = () => {
        setShowCreateAddress(false)
        setShowCreateContact(true)
    }

    const handleCreateAddress = (created_obj? : any) => {
        setShowCreateAddress(false)
        if(created_obj){
            setAddressList([created_obj,...addressList])
            // Don't auto-select the newly created address
        }
    }

    const handleCreateContact = (created_obj? : any) => {
        setShowCreateContact(false)
        if(created_obj){
            setContactList([created_obj, ...contactList])
            // Don't auto-select the newly created contact
        }
    }

    const closeCreateAddress = () => {
        setShowCreateAddress(false)
    }

    const closeCreateContact = () => {
        setShowCreateContact(false)
    }

    const handleSubmit = async(values: PartialClient) => {
        try {
            let response = undefined
            if(isUpdating){
                let data: any = {...values}
                // Ensure contact and address are string IDs
                if (typeof data.contact === 'object' && data.contact !== null) {
                    data.contact = data.contact.id
                }
                if (typeof data.address === 'object' && data.address !== null) {
                    data.address = data.address.id
                }
                response = await updateClient(props.clientToUpdate?.id? props.clientToUpdate.id.toString(): '', data)
            }
            else {
                let data: any = {...values}
                // Ensure contact and address are string IDs
                if (typeof data.contact === 'object' && data.contact !== null) {
                    data.contact = data.contact.id
                }
                if (typeof data.address === 'object' && data.address !== null) {
                    data.address = data.address.id
                }
                response = await postClient(data)
            }
            if (response && !Array.isArray(response)) {
                setErrorMessages([])
                setShowSuccess(true)
                if (formikRef.current) formikRef.current.resetForm()
                if (contactSelectRef.current) contactSelectRef.current.clearValue()
                if (addressSelectRef.current) addressSelectRef.current.clearValue()
                if (props.onCreate && !isUpdating) {
                    let dataToReturn = response.data
                    // Get the address ID whether it's an object or string
                    const addressId = typeof values.address === 'object' && values.address !== null 
                        ? values.address.id 
                        : values.address
                    // Get the contact ID whether it's an object or string
                    const contactId = typeof values.contact === 'object' && values.contact !== null 
                        ? values.contact.id 
                        : values.contact

                    dataToReturn.address = addressId ? addressList.find(address => address.id.toString() === addressId.toString()) : undefined
                    dataToReturn.contact = contactId ? contactList.find(contact => contact.id.toString() === contactId.toString()) : undefined
                    props.onCreate(dataToReturn)
                }
                if(isUpdating && props.onUpdate) {
                    let dataToReturn = response
                    // Get the address ID whether it's an object or string
                    const addressId = typeof values.address === 'object' && values.address !== null 
                        ? values.address.id 
                        : values.address
                    // Get the contact ID whether it's an object or string
                    const contactId = typeof values.contact === 'object' && values.contact !== null 
                        ? values.contact.id 
                        : values.contact

                    dataToReturn.address = addressId ? addressList.find(address => address.id.toString() === addressId.toString()) : undefined
                    dataToReturn.contact = contactId ? contactList.find(contact => contact.id.toString() === contactId.toString()) : undefined
                    props.onUpdate(dataToReturn)
                }
            }
        }
        catch (error) {
            setErrorMessages(handlePostError(error, 'Address'))
        }
    }

    if (isLoading) {
        return (isLoadingSpinner());
    }

    return (
        <Container>
            <Row className={"justify-content-center"}>
                {errorMessages.length > 0 && (
                    <Alert variant={'danger'}>
                        <ul>
                            {errorMessages.map((message, index) => (
                                <li key={index}>{message}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {showSuccess && (
                    <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                        Client created successfully!
                    </Alert>
                )}
            </Row>
            <Row className={'justify-content-center'}>
                <Formik
                    enableReinitialize={true}
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {formik => (
                        <Form onSubmit={formik.handleSubmit} autoComplete="off">
                            <Form.Group as={Row} className="mb-3" controlId="client_name">
                                <Form.Label column className={'fw-bold'} sm={2}>
                                    Client Name
                                </Form.Label>
                                <Col sm={10}>
                                    <Field name={'client_name'} type={'text'} placeholder={"Suncor Energy"} as={Form.Control} />
                                    <ErrorMessage name={'client_name'} component={'div'} className={'text-danger'}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3" controlId="contact">
                                <Form.Label column className={'fw-bold'} xs={2}>
                                    Contact
                                </Form.Label>
                                <Col>
                                    <Select
                                        ref={contactSelectRef}
                                        name="contact"
                                        defaultValue={initialValues.contact? ({
                                            label: initialValues.contact.contact_name,
                                            value: initialValues.contact.id
                                        }):undefined}
                                        options={contactList.map(entry => ({
                                            label: entry.contact_name,
                                            value: entry.id
                                        })).sort((a, b) => a.label.localeCompare(b.label))}
                                        onChange={(selectedOptions) => {
                                            if (selectedOptions && selectedOptions.value) {
                                                const value = selectedOptions.value as string | number;
                                                const selectedContact = contactList.find(contact => contact.id.toString() === value.toString());
                                                formik.setFieldValue('contact', selectedContact || selectedOptions.value);
                                            } else {
                                                formik.setFieldValue('contact', '');
                                            }
                                        }}
                                    />
                                </Col>
                                <Col xs={2}>
                                    <Button onClick={handleShowCreateContact}>Add New Contact</Button>
                                </Col>
                                <ErrorMessage name="contact" component="div" className="text-danger" />
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="address">
                                <Form.Label column className={'fw-bold'} xs={2}>
                                    Address
                                </Form.Label>
                                <Col>
                                    <Select
                                        ref={addressSelectRef}
                                        name="address"
                                        defaultValue={initialValues.address? ({
                                            label: initialValues.address.label? (initialValues.address.label):(initialValues.address.street),
                                            value: initialValues.address.id
                                        }):undefined}
                                        options={addressList.map(entry => ({
                                            label: entry.label? `${entry.label} - ${entry.street}`:entry.street,
                                            value: entry.id
                                        })).sort((a, b) => a.label.localeCompare(b.label))}
                                        onChange={(selectedOptions) => {
                                            if (selectedOptions && selectedOptions.value) {
                                                const value = selectedOptions.value as string | number;
                                                const selectedAddress = addressList.find(address => address.id.toString() === value.toString());
                                                formik.setFieldValue('address', selectedAddress || selectedOptions.value);
                                            } else {
                                                formik.setFieldValue('address', '');
                                            }
                                        }}
                                    />
                                </Col>
                                <Col xs={2}>
                                    <Button onClick={handleShowCreateAddress}>Add New Address</Button>
                                </Col>
                                <ErrorMessage name="address" component="div" className="text-danger" />
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Col>
                                    <Button type="submit" disabled={formik.isSubmitting}>Submit</Button>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Row>

            <Modal show={showCreateContact} onHide={handleCloseCreateContact} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Contact</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateContact closeModal={closeCreateContact} onCreate={handleCreateContact}/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreateContact}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showCreateAddress} onHide={closeCreateAddress} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Address</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateAddress closeModal={closeCreateAddress} onCreate={handleCreateAddress}/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeCreateAddress}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>

    );
}

export default CreateClient;
